import React, { useCallback, useRef, useState } from 'react';
import styles from './multiSplicedCreativeDetail.module.scss';
import {
  CreativeListRecord,
  CreativeStatus,
  OutdoorType
} from 'core/creative/Creative';
import ToggleButtons from 'components/common/ToggleButtons/ToggleButtons';
import cx from 'classnames/bind';
import i18n from 'i18n';
import { ScreenDisplayType } from 'core/channelGroup/ChannelGroup';
import { get, isEmpty } from 'lodash';
import { OutdoorCreativeDetail } from './OutdoorCreativeDetail';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
const classNames = cx.bind(styles);

const MAX_CONTAINER_WIDTH = 600;
const MAX_CONTAINER_HEIGHT = 337;
const GRID_GAP = 10;

export const MultiSplicedCreativeDetail: React.FC<{
  creative: Partial<CreativeListRecord>;
}> = ({ creative }) => {
  const [screenDisplayType, setScreenDisplayType] = useState(
    ScreenDisplayType.SINGLE
  );
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const creativeExtra = get(creative, 'bannerExtra');

  const originalVideoUrl = get(creativeExtra, 'originalVideoUrl');
  const singleDetailCreative = originalVideoUrl
    ? {
        ...creative,
        creativeValue: {
          ...get(creative, 'creativeValue', get(creative, 'creativeValues')),
          videoUrl: originalVideoUrl
        }
      }
    : creative;

  const updatePreviewType = useCallback((displayType) => {
    setScreenDisplayType(displayType as ScreenDisplayType);
  }, []);

  const singleDetailClass = classNames('singleDetailClass', {
    show: screenDisplayType === ScreenDisplayType.SINGLE
  });

  const multiSplicedDetailClass = classNames('multiSplicedDetailClass', {
    show: screenDisplayType === ScreenDisplayType.MULTI_SPLICED
  });

  const handlePlayAll = useCallback(() => {
    const videos = videoRefs.current.filter(Boolean) as HTMLVideoElement[];
    if (isPlaying) {
      videos.forEach((v) => v.pause());
      setIsPlaying(false);
    } else {
      const playPromises = videos.map((v, index) => {
        if (index !== 0) {
          v.muted = true;
        }
        return v.play();
      });
      Promise.all(playPromises)
        .then(() => setIsPlaying(true))
        .catch(() => {
          /* no-op */
        });
    }
  }, [isPlaying]);

  const creativeTypeSetting = ChannelGroupSetting.creativeTypeSettings().get(
    creative.creativeType!
  );
  if (!creativeTypeSetting) {
    return <div />;
  }

  const channelGroupSetting = ChannelGroupSetting.fromId(
    creativeTypeSetting.channelGroupId
  );
  const screenProperty = channelGroupSetting.config.screenProperty;
  if (!screenProperty) {
    return <div />;
  }

  const {
    screenPixelWidth,
    screenPixelHeight,
    screenSplicedHorizontalScreenCount,
    screenSplicedVerticalScreenCount
  } = screenProperty;

  const outdoorType = get(creative, 'outdoorType', OutdoorType.VIDEO);
  const isVideo = [OutdoorType.VIDEO, OutdoorType.VIDEO_IMAGE].includes(
    outdoorType
  );
  const cropPaths: string[] = isVideo
    ? (get(creativeExtra, 'videoCropPaths', []) as string[])
    : (get(creativeExtra, 'imageCropPaths', []) as string[]);
  const processing =
    [CreativeStatus.PROCESSING_FAIL, CreativeStatus.PROCESSING].includes(
      creative.creativeState!
    ) || isEmpty(cropPaths);
  const multiSplicedToggleLabel = processing
    ? `${i18n.t(
        `multiSplicedCreativeDetail.labels.toggleMultiSpliced${isVideo ? 'Video' : 'Image'}`
      )} (${i18n.t('creative.status.processing')})`
    : i18n.t(
        `multiSplicedCreativeDetail.labels.toggleMultiSpliced${isVideo ? 'Video' : 'Image'}`
      );

  const displayOptions = [
    {
      label: i18n.t(
        `multiSplicedCreativeDetail.labels.toggleSingle${isVideo ? 'Video' : 'Image'}`
      ),
      value: ScreenDisplayType.SINGLE
    },
    {
      label: multiSplicedToggleLabel,
      value: ScreenDisplayType.MULTI_SPLICED
    }
  ];
  const totalCount =
    screenSplicedHorizontalScreenCount * screenSplicedVerticalScreenCount;

  const totalGridWidth =
    screenPixelWidth * screenSplicedHorizontalScreenCount +
    GRID_GAP * (screenSplicedHorizontalScreenCount - 1);
  const totalGridHeight =
    screenPixelHeight * screenSplicedVerticalScreenCount +
    GRID_GAP * (screenSplicedVerticalScreenCount - 1);

  const scaleX = MAX_CONTAINER_WIDTH / totalGridWidth;
  const scaleY = MAX_CONTAINER_HEIGHT / totalGridHeight;
  const scale = Math.min(scaleX, scaleY, 1);

  const setVideoRef = (index: number) => (el: HTMLVideoElement | null) => {
    videoRefs.current[index] = el;
  };

  const renderMediaGrid = () => {
    const cells: JSX.Element[] = [];
    const onVideoEnded = () => {
      setIsPlaying(false);
    };
    for (let row = 0; row < screenSplicedVerticalScreenCount; row++) {
      for (let col = 0; col < screenSplicedHorizontalScreenCount; col++) {
        const index = row * screenSplicedHorizontalScreenCount + col;
        const cropUrl = cropPaths[index];
        if (!cropUrl) continue;
        cells.push(
          <div
            key={index}
            className={styles.videoCell}
            style={{
              width: screenPixelWidth,
              height: screenPixelHeight
            }}
          >
            {isVideo ? (
              <video
                ref={setVideoRef(index)}
                className={styles.cellVideo}
                loop
                onEnded={onVideoEnded}
              >
                <source src={cropUrl} type='video/mp4' />
              </video>
            ) : (
              <img
                className={styles.cellVideo}
                src={cropUrl}
                alt={`${row + 1}-${col + 1}`}
              />
            )}
            <span className={styles.cellLabel}>
              {row + 1}-{col + 1}
            </span>
          </div>
        );
      }
    }
    return cells;
  };

  return (
    <div className={styles.multiSplicedCreativeDetail}>
      <ToggleButtons
        className={styles.toggleButtons}
        name={'screenDisplayType'}
        value={screenDisplayType}
        options={displayOptions}
        onChange={updatePreviewType}
        disabledValues={
          processing ? [ScreenDisplayType.MULTI_SPLICED] : undefined
        }
      />
      <div className={singleDetailClass}>
        <OutdoorCreativeDetail creative={singleDetailCreative} />
        <div className={styles.bottomArea} />
      </div>
      <div className={multiSplicedDetailClass}>
        {cropPaths.length > 0 && totalCount > 0 && (
          <div className={styles.gridContainer}>
            <div className={styles.gridScrollArea}>
              <div
                className={styles.gridScaleWrapper}
                style={{
                  width: totalGridWidth * scale,
                  height: totalGridHeight * scale
                }}
              >
                <div
                  className={styles.videoGrid}
                  style={{
                    gridTemplateColumns: `repeat(${screenSplicedHorizontalScreenCount}, ${screenPixelWidth}px)`,
                    gridTemplateRows: `repeat(${screenSplicedVerticalScreenCount}, ${screenPixelHeight}px)`,
                    gap: GRID_GAP,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    width: totalGridWidth,
                    height: totalGridHeight
                  }}
                >
                  {renderMediaGrid()}
                </div>
              </div>
            </div>
            <div className={styles.bottomArea}>
              {isVideo && (
                <button
                  className='btn btn-secondary btn-sm'
                  onClick={handlePlayAll}
                >
                  {isPlaying
                    ? i18n.t('multiSplicedCreativeDetail.labels.pauseAll')
                    : i18n.t('multiSplicedCreativeDetail.labels.playAll')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
